import type { BrandAlertsPlayer } from './types';

/**
 * Platform lifecycle signals: page close, viewport resize, focus, fullscreen,
 * and (iOS) the native handlebar.
 *
 * They are event sources, so they belong behind their own interface rather than inside the client.
 * Putting them here is what makes `destroy()` possible.
 */
export interface SignalHandlers {
    pageClose(): void;
    resize(): void;
    focusLost(): void;
    focusGained(): void;
    fullscreenEnter(): void;
    handlebarExpanded(): void;
    handlebarCollapsed(): void;
}

export interface PlatformSignals {
    subscribe(handlers: SignalHandlers): void;
    destroy(): void;
}

/**
 * Tracks its own listeners so they can all be removed on destroy.
 */
abstract class BaseSignals implements PlatformSignals {
    private readonly _teardown: (() => void)[] = [];

    abstract subscribe(handlers: SignalHandlers): void;

    destroy(): void {
        this._teardown.forEach((off) => off());
        this._teardown.length = 0;
    }

    protected onWindow(event: string, handler: () => void): void {
        window.addEventListener(event, handler);
        this._teardown.push(() => window.removeEventListener(event, handler));
    }
}

/**
 * Web: real browser events plus the Brightcove player's fullscreen event.
 */
export class WebSignals extends BaseSignals {
    constructor(private readonly _player: BrandAlertsPlayer) {
        super();
    }

    override subscribe(handlers: SignalHandlers): void {
        this.onWindow('beforeunload', () => handlers.pageClose());
        this.onWindow('resize', () => handlers.resize());
        this.onWindow('blur', () => handlers.focusLost());
        this.onWindow('focus', () => handlers.focusGained());

        // The player wrapper exposes no `off`, so this one cannot be torn down —
        // the guard in the handler is what keeps it harmless after destroy().
        this._player.on('fullscreenchange', () => {
            if (this._player.isFullscreen()) handlers.fullscreenEnter();
        });
    }
}

/**
 * iOS: the native app posts events into the webview.
 *
 * `resize` is deliberately never emitted — the native side reports a short
 * screen once via `ios_screen_too_small`, which is a blocker, and the iOS
 * sideguide has never resized or dismissed on viewport change.
 */
export class IosSignals extends BaseSignals {
    override subscribe(handlers: SignalHandlers): void {
        this.onWindow('ios_unload', () => handlers.pageClose());
        this.onWindow('ios_focus_lost', () => handlers.focusLost());
        this.onWindow('ios_focus_retrieved', () => handlers.focusGained());
        this.onWindow('ios_fullscreen_enter', () => handlers.fullscreenEnter());
        this.onWindow('ios_handlebar_expanded', () => handlers.handlebarExpanded());
        this.onWindow('ios_handlebar_collapsed', () => handlers.handlebarCollapsed());
    }
}
